import React, { useState, useEffect, useRef } from 'react';
import { formatDate, parseSmartDate, toStorageDate } from '../lib/dateUtils';
import { useStore } from '../store/useStore';
import { useSystemDate } from '../hooks/useSystemDate';

interface DateInputProps {
  value: string; // YYYY-MM-DD storage format
  onChange: (value: string) => void;
  className?: string;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onConfirm?: () => void; // called after Enter confirms (for focus-chaining)
  placeholder?: string;
  nextRef?: React.RefObject<HTMLInputElement>; // focus this after Enter
}

/**
 * Aarthika Pro-style Smart Date Input.
 *
 * Accepts any shorthand the user might type:
 *   "1"         → 1-<curr month>-<curr year>
 *   "1-6"       → 1-6-<curr year>
 *   "1jun"      → 1-6-<curr year>
 *   "1 jun 26"  → 1-6-2026
 *   "1-6-26"    → 1-6-2026
 *   "1jun26"    → 1-6-2026
 *   "010626"    → 1-6-2026
 *   "t"/"today" → today
 *
 * Displays: D-M-YYYY (Aarthika style, no leading zeros)
 * Stores:   YYYY-MM-DD
 */
export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(({
  value,
  onChange,
  className = '',
  autoFocus = false,
  onKeyDown,
  onConfirm,
  placeholder = 'D-M-YYYY',
  nextRef,
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
  const systemDate = useSystemDate();

  // Sync display when external value changes
  useEffect(() => {
    setInputValue(value ? formatDate(value) : '');
  }, [value]);

  const getBaseDate = (): Date => {
    try { return new Date(systemDate); } catch { return new Date(); }
  };

  /** Parse + commit. Returns true on success, false if invalid (reverts). */
  const commit = (): boolean => {
    const raw = inputValue.trim();
    if (!raw) { onChange(''); return true; }
    const parsed = parseSmartDate(raw, getBaseDate());
    if (parsed) {
      onChange(toStorageDate(parsed));
      setInputValue(formatDate(parsed));
      return true;
    }
    // Invalid — revert
    setInputValue(value ? formatDate(value) : '');
    return false;
  };

  const handleBlur = () => { commit(); };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const ok = commit();
      if (ok) {
        onConfirm?.();
        if (nextRef?.current) setTimeout(() => nextRef!.current!.focus(), 0);
      }
    } else if (e.key === 'Escape') {
      setInputValue(value ? formatDate(value) : '');
    }
    onKeyDown?.(e);
  };

  const handleFocus = () => {
    // Select all on focus — Aarthika style (user can overtype immediately)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  };

  // When autoFocus=true (F2 press), select all text so user can type new date immediately
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      className={`outline-none px-2 py-1 border border-gray-300 focus:border-[#1e8585] focus:ring-1 focus:ring-[#1e8585] transition-all ${className}`}
      autoFocus={autoFocus}
      placeholder={placeholder}
      spellCheck={false}
      autoComplete="off"
    />
  );
});

DateInput.displayName = 'DateInput';
